import { VideoData } from '@anm/hosting-player';
import * as analytic from '@anm/analytic';
import { FormatNames, Landing } from '@anm/api/modules/hosting';
import { HIDE_VIEW_FORMATS_DELAY } from '@anm/constants/delays';
import { DEFAULT_MOBILE_HEIGHT, DEFAULT_MOBILE_WIDTH, DEFAULT_VIEW_TYPE } from '@anm/constants/videoLandings';
import useHover from '@anm/hooks/useHover';
import { useCallback, useEffect, useRef, useState, RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hostedVideoActions, hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { landingsActions, landingsSelectors } from '../../../../store/modules/landings';
import { userSelectors } from '../../../../store/modules/user';

type UseIframeCommunicationProps = {
  iframeRef: RefObject<HTMLIFrameElement>;
  landing?: Landing;
  viewType?: FormatNames;
};

type MessageItem = {
  landing?: Landing;
  videoData?: VideoData;
  viewType?: FormatNames;
};

export const useIframeCommunication = ({ landing, viewType, iframeRef }: UseIframeCommunicationProps) => {
  const videoData = useSelector(hostedVideoSelectors.selectVideoData);

  const sendMessage = useCallback((item: MessageItem) => iframeRef.current?.contentWindow?.postMessage(item, '*'), [
    landing,
    videoData,
    viewType
  ]);

  useEffect(() => {
    if (!iframeRef.current || !landing) return;

    sendMessage({ landing });
  }, [iframeRef, landing]);

  useEffect(() => {
    if (!iframeRef.current || !videoData) return;

    sendMessage({ videoData });
  }, [iframeRef, videoData]);

  useEffect(() => {
    sendMessage({ viewType });
  }, [viewType]);
};

export const useTrackOpenPreview = (isPreviewScreen: boolean) => {
  useEffect(() => {
    isPreviewScreen && analytic.trackOpenVideoPreview();
  }, []);
};

export const useIframeSizes = (containerRef: RefObject<HTMLDivElement>, isLandingScreen: boolean) => {
  const getIframeWidthByViewType = useCallback(
    (type: FormatNames) => {
      const viewTypeMap: { [key in FormatNames]: number } = {
        mobile: DEFAULT_MOBILE_WIDTH,
        fit: containerRef?.current?.offsetWidth || 0,
        fullscreen: document.documentElement.offsetWidth
      };

      return viewTypeMap[type];
    },
    [containerRef?.current, window.screen.width]
  );

  const dispatch = useDispatch();
  const currentViewType = useSelector(landingsSelectors.selectLandingViewType) || DEFAULT_VIEW_TYPE;

  const [viewType, setViewType] = useState<FormatNames>(currentViewType);
  const [iframeWidth, setIframeWidth] = useState<number>(getIframeWidthByViewType(viewType));
  const [iframeHeight, setIframeHeight] = useState<number>(viewType === 'mobile' ? DEFAULT_MOBILE_HEIGHT : 0);

  const onReceiveMessage = useCallback(
    ({ data: { minHeight } }: MessageEvent) => {
      if (!minHeight) return;

      setIframeHeight(minHeight);
    },
    [iframeHeight]
  );

  useEffect(() => {
    window.addEventListener('message', onReceiveMessage);

    return () => {
      window.removeEventListener('message', onReceiveMessage);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || !isLandingScreen) return;

    const newWidth = getIframeWidthByViewType(viewType);

    iframeWidth !== newWidth && setIframeWidth(newWidth);
  }, [viewType, isLandingScreen, containerRef.current]);

  const handleTypeChange = useCallback(
    (type: FormatNames) => {
      setViewType(type);
      dispatch(landingsActions.updateLandingViewType(type));
    },
    [viewType]
  );

  return [iframeWidth, iframeHeight, viewType, handleTypeChange] as const;
};

export const useLandingPreviewHover = () => {
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  const [prevHovered, setPrevHovered] = useState(false);

  useEffect(() => {
    isHovered && !prevHovered && setPrevHovered(true);
  }, [isHovered]);

  useEffect(() => {
    if (isHovered || !prevHovered) return;

    const timerId = setTimeout(() => setPrevHovered(false), HIDE_VIEW_FORMATS_DELAY);

    return () => clearTimeout(timerId);
  }, [isHovered, prevHovered]);

  return [hoverRef, prevHovered] as const;
};

export const useTurnOffFooter = (isLandingScreen: boolean, landing?: Landing) => {
  const [isFooterDisabled, setIsFooterDisabled] = useState(false);
  const [isIframeMounted, setIsIframeMounted] = useState(false);
  const isUserFree = useSelector(userSelectors.selectIsUserFree);
  const initialLanding = useRef<Landing | null>(null);
  const dispatch = useDispatch();

  const onReceiveMessage = useCallback(({ data }: MessageEvent) => {
    data.mounted && setIsIframeMounted(true);
  }, []);

  useEffect(() => {
    window.addEventListener('message', onReceiveMessage);

    return () => window.removeEventListener('message', onReceiveMessage);
  }, []);

  useEffect(() => {
    if (initialLanding.current || !landing) return;

    initialLanding.current = landing;
  }, [landing, initialLanding.current]);

  useEffect(() => {
    if (!initialLanding.current || isFooterDisabled || !isIframeMounted || !isLandingScreen) return;

    const isFooterEnabled = !!initialLanding.current.parameters?.hasFooter;
    const canDisableFooter = isFooterEnabled && !isUserFree;

    if (!canDisableFooter) return;

    const updatedLanding: Landing = {
      ...initialLanding.current,
      parameters: {
        ...landing?.parameters,
        hasFooter: false
      }
    };

    dispatch(hostedVideoActions.toggleNotifications(false));
    dispatch(landingsActions.updateLanding(updatedLanding));
    dispatch(
      landingsActions.saveLanding({
        public: updatedLanding.public,
        hostId: updatedLanding.hostId,
        parameters: updatedLanding.parameters
      })
    );

    setIsFooterDisabled(true);
  }, [initialLanding.current, isFooterDisabled, isIframeMounted, isLandingScreen]);
};
